import { defineCustomElement, ref, computed, watch, nextTick } from 'vue/dist/vue.esm-bundler.js'
import { html } from './shared/functions'
import { jsonFetch } from '../shared/turboHelper'

const ImComponent = defineCustomElement({
  props: {
    conversationId: {
      type: [String, Number],
      default: null
    },
    provider: {
      type: String,
      default: 'ollama'
    },
    model: {
      type: String,
      default: 'llama3.2'
    },
    oneShot: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: '메시지를 입력하세요...'
    },
    height: {
      type: String,
      default: '500px'
    }
  },
  setup(props) {
    const messages = ref([])
    const inputMessage = ref('')
    const isLoading = ref(false)
    const isConnected = ref(false)
    const cable = ref(null)
    const subscription = ref(null)
    const messagesContainer = ref(null)

    // Rails ActionCable 연결
    const connectCable = () => {
      if (window.ActionCable && props.conversationId && !props.oneShot) {
        cable.value = window.ActionCable.createConsumer('/cable')
        subscription.value = cable.value.subscriptions.create(
          {
            channel: 'AgenticChannel',
            conversation_id: props.conversationId
          },
          {
            connected() {
              isConnected.value = true
              console.log('Agentic Chat 연결됨')
            },
            disconnected() {
              isConnected.value = false
              console.log('Agentic Chat 연결 해제됨')
            },
            received(data) {
              handleCableMessage(data)
            }
          }
        )
      }
    }

    // ActionCable 메시지 처리
    const handleCableMessage = (data) => {
      switch (data.type) {
        case 'user_message':
          // 사용자 메시지는 이미 추가되어 있음
          break
        case 'chunk':
          // 스트리밍 청크 처리
          updateLastAssistantMessage(data.content)
          break
        case 'final_response':
          // 최종 응답
          addMessage('assistant', data.content, { final: true })
          isLoading.value = false
          break
        case 'status':
          // 상태 업데이트 (처리 중, 도구 실행 중 등)
          updateStatus(data.status)
          break
        case 'tool_execution':
          // 도구 실행 상태
          updateToolExecution(data)
          break
        case 'error':
          // 오류 처리
          addMessage('error', data.message)
          isLoading.value = false
          break
      }
      scrollToBottom()
    }

    // 메시지 추가
    const addMessage = (role, content, metadata = {}) => {
      const message = {
        id: Date.now() + Math.random(),
        role,
        content,
        timestamp: new Date(),
        ...metadata
      }
      messages.value.push(message)
    }

    // 마지막 어시스턴트 메시지 업데이트 (스트리밍용)
    const updateLastAssistantMessage = (chunk) => {
      const lastMessage = messages.value[messages.value.length - 1]
      if (lastMessage && lastMessage.role === 'assistant' && !lastMessage.final) {
        lastMessage.content += chunk
      } else {
        addMessage('assistant', chunk, { streaming: true })
      }
    }

    // 상태 업데이트
    const updateStatus = (status) => {
      const statusMessages = {
        processing: '🤔 생각 중...',
        tool_calling: '🔧 도구 실행 중...',
        executing_tools: '⚙️ 작업 실행 중...',
        completed: '✅ 완료'
      }

      // 기존 상태 메시지 제거
      messages.value = messages.value.filter(m => m.role !== 'status')

      if (status !== 'completed') {
        addMessage('status', statusMessages[status] || status)
      }
    }

    // 도구 실행 상태 업데이트
    const updateToolExecution = (data) => {
      const { tool_name, status, result } = data
      const statusEmojis = {
        started: '🔧',
        completed: '✅',
        failed: '❌'
      }

      const message = `${statusEmojis[status]} ${tool_name}: ${status}`
      addMessage('tool', message, { tool_name, status, result })
    }

    // 메시지 전송
    const sendMessage = async () => {
      if (!inputMessage.value.trim()) return

      const content = inputMessage.value.trim()
      inputMessage.value = ''
      isLoading.value = true

      // 사용자 메시지 추가
      addMessage('user', content)

      try {
        if (props.oneShot) {
          // 1회성 질의
          await sendOneShotMessage(content)
        } else {
          // 대화형 메시지
          await sendChatMessage(content)
        }
      } catch (error) {
        addMessage('error', `오류: ${error.message}`)
        isLoading.value = false
      }
    }

    // 1회성 메시지 전송
    const sendOneShotMessage = async (content) => {
      const response = await jsonFetch('/agent/one_shot_query', {
        method: 'POST',
        body: {
          content,
          provider: props.provider,
          model: props.model
        }
      })

      const data = await response.json()

      if (data.success) {
        addMessage('assistant', data.response, { final: true })
      } else {
        addMessage('error', data.error || '응답 생성 중 오류가 발생했습니다.')
      }

      isLoading.value = false
    }

    // 대화형 메시지 전송
    const sendChatMessage = async (content) => {
      if (subscription.value) {
        subscription.value.send({
          action: 'send_message',
          conversation_id: props.conversationId,
          content,
          provider: props.provider,
          model: props.model,
          streaming: true
        })
      }
    }

    // 스크롤 맨 아래로
    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
        }
      })
    }

    // 엔터키 처리
    const handleKeypress = (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        sendMessage()
      }
    }

    // 메시지 시간 포맷
    const formatTime = (timestamp) => {
      return new Date(timestamp).toLocaleTimeString('ko-KR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // 컴포넌트 마운트시
    const onMounted = () => {
      if (!props.oneShot) {
        connectCable()
      }
    }

    // 컴포넌트 언마운트시
    const onUnmounted = () => {
      if (subscription.value) {
        subscription.value.unsubscribe()
      }
      if (cable.value) {
        cable.value.disconnect()
      }
    }

    return {
      messages,
      inputMessage,
      isLoading,
      isConnected,
      messagesContainer,
      sendMessage,
      handleKeypress,
      formatTime,
      onMounted,
      onUnmounted
    }
  },

  mounted() {
    this.onMounted()
  },

  unmounted() {
    this.onUnmounted()
  },

  template: html`
    <div class="agentic-chat-container" :style="{ height: height }">
      <!-- 헤더 -->
      <div class="chat-header bg-gray-100 dark:bg-gray-800 p-3 border-b">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <div class="w-3 h-3 rounded-full" :class="isConnected ? 'bg-green-500' : 'bg-red-500'"></div>
            <span class="text-sm font-medium">AI 어시스턴트</span>
            <span class="text-xs text-gray-500">({{ provider }} - {{ model }})</span>
          </div>
          <div v-if="oneShot" class="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
            1회성 질의
          </div>
        </div>
      </div>
      
      <!-- 메시지 영역 -->
      <div 
        ref="messagesContainer"
        class="chat-messages flex-1 overflow-y-auto p-4 space-y-4"
        style="height: calc(100% - 120px);"
      >
        <div v-for="message in messages" :key="message.id" class="message">
          <!-- 사용자 메시지 -->
          <div v-if="message.role === 'user'" class="flex justify-end">
            <div class="bg-blue-600 text-white rounded-lg px-4 py-2 max-w-xs lg:max-w-md">
              <p class="text-sm whitespace-pre-line">{{ message.content }}</p>
              <p class="text-xs opacity-75 mt-1">{{ formatTime(message.timestamp) }}</p>
            </div>
          </div>
          
          <!-- AI 어시스턴트 메시지 -->
          <div v-else-if="message.role === 'assistant'" class="flex justify-start">
            <div class="bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-4 py-2 max-w-xs lg:max-w-md">
              <p class="text-sm whitespace-pre-line">{{ message.content }}</p>
              <p class="text-xs opacity-75 mt-1">{{ formatTime(message.timestamp) }}</p>
            </div>
          </div>
          
          <!-- 상태 메시지 -->
          <div v-else-if="message.role === 'status'" class="flex justify-center">
            <div class="bg-yellow-100 text-yellow-800 rounded-lg px-3 py-1 text-xs">
              {{ message.content }}
            </div>
          </div>
          
          <!-- 도구 실행 메시지 -->
          <div v-else-if="message.role === 'tool'" class="flex justify-center">
            <div class="bg-purple-100 text-purple-800 rounded-lg px-3 py-1 text-xs">
              {{ message.content }}
            </div>
          </div>
          
          <!-- 오류 메시지 -->
          <div v-else-if="message.role === 'error'" class="flex justify-center">
            <div class="bg-red-100 text-red-800 rounded-lg px-3 py-1 text-xs">
              {{ message.content }}
            </div>
          </div>
        </div>
        
        <!-- 로딩 인디케이터 -->
        <div v-if="isLoading" class="flex justify-start">
          <div class="bg-gray-200 dark:bg-gray-700 rounded-lg px-4 py-2">
            <div class="flex space-x-1">
              <div class="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
              <div class="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
              <div class="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 입력 영역 -->
      <div class="chat-input border-t p-3">
        <div class="flex space-x-2">
          <textarea
            v-model="inputMessage"
            @keypress="handleKeypress"
            :placeholder="placeholder"
            :disabled="isLoading"
            class="flex-1 resize-none border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            rows="1"
          ></textarea>
          <button
            @click="sendMessage"
            :disabled="!inputMessage.trim() || isLoading"
            class="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            전송
          </button>
        </div>
      </div>
    </div>
  `,

  styles: html`
    <style>
    .agentic-chat-container {
      display: flex;
      flex-direction: column;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      background: white;
      font-family: system-ui, -apple-system, sans-serif;
    }
    
    .dark .agentic-chat-container {
      background: #1f2937;
      border-color: #374151;
    }
    
    .chat-messages::-webkit-scrollbar {
      width: 6px;
    }
    
    .chat-messages::-webkit-scrollbar-track {
      background: #f1f5f9;
    }
    
    .chat-messages::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;
    }
    
    .chat-messages::-webkit-scrollbar-thumb:hover {
      background: #94a3b8;
    }
    </style>
  `
}, { shadowRoot: false })

customElements.define('im-agentic-chat', ImComponent)
